import { useHover } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { useEffect, useRef, useState } from 'react';
import { ButtonBase } from '../../components/ButtonBase';
import { Icon, IconSize } from '../../components/Icon';
import { Text } from '../../components/Text';
import { identifyComponent } from '../../util';
import { Box, BoxProps } from '../Box';
import { Flex } from '../Flex';
import css from './index.module.scss';

export interface SpinnerProps {
  testId?: string;
  variant?: 'dark' | 'light';
  diameter?: number;
  component?: React.ElementType;
}

export const Spinner: React.FunctionComponent<SpinnerProps> = ({
  testId = 'spinner',
  variant = 'dark',
  diameter = 22,
  component: Component = 'div',
  ...rest
}) => {
  const classes = classNames(css.spinnerComponent, css[variant]);
  const style = {
    height: `${diameter}px`,
    width: `${diameter}px`,
  };

  return (
    <Component
      data-component="Spinner"
      data-testid={testId}
      className={classes}
      style={style}
      {...rest}
      {...identifyComponent('spinner', rest)}
    />
  );
};

interface SpinnerFailedProps {
  testId?: string;
  size?: IconSize;
  failed: boolean;
  onFailedRetry: () => void;
  failedMessage?: string;
}

export const SpinnerWithFailed: React.FunctionComponent<SpinnerFailedProps> = ({
  testId,
  size = 20,
  failed,
  onFailedRetry,
  failedMessage,
}) => {
  const identityProps = identifyComponent('spinner-with-failed');
  const { hovered, hoverProps } = useHover();
  if (failed) {
    return (
      <ButtonBase
        data-component="SpinnerWithFailed"
        onClick={onFailedRetry}
        {...hoverProps}
        {...identityProps}
      >
        <Flex alignItems="center" justifyContent="flex-start" gap={4}>
          <Icon
            name={hovered ? 'refresh' : 'warning'}
            color={hovered ? 'cta' : 'secondary'}
            size={size}
          />
          {failedMessage && (
            <Text appearance="bodySmall" color="secondary">
              {failedMessage}
            </Text>
          )}
        </Flex>
      </ButtonBase>
    );
  }
  return (
    <Spinner
      data-component="SpinnerWithFailed"
      testId={testId}
      diameter={size}
      {...identityProps}
    />
  );
};

export const PageSpinner: React.FC<
  SpinnerProps & Pick<React.ComponentProps<typeof Box>, 'padding'>
> = ({ diameter = 60, padding = 64, variant, testId }) => (
  <Box
    data-component="PageSpinner"
    width={1}
    padding={padding}
    {...identifyComponent('page-spinner')}
  >
    <Flex>
      <Spinner diameter={diameter} variant={variant} testId={testId} />
    </Flex>
  </Box>
);

const defaultSpinnerDelay = 500;

interface OverlaySpinnerProps extends Omit<SpinnerProps, 'variant'> {
  /**
   * The delay in milliseconds before the spinner is shown. This is to prevent the spinner from popping up for quick actions.
   * @default 500
   */
  delay?: number;
  /**
   * Whether or not the spinner should be shown.
   * @default false
   */
  loading: boolean;
}
/**
 * Loading indicator that is placed over the top of children. This is useful for when you want to prevent
 * interaction with the children while loading. The spinner will only show if the `loading` prop is true
 * and the `delay` has passed. This is to prevent the spinner from popping up for quick actions.
 */
export const OverlaySpinner: React.FC<OverlaySpinnerProps & BoxProps> = ({
  delay = defaultSpinnerDelay,
  loading = false,
  diameter = 64,
  children,
  className,
  ...rest
}) => {
  const [active, setActive] = useState(false);
  const delayTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * When the loading prop changes, we want to set a timeout to show the spinner after the delay.
   * If the loading prop changes before the timeout is reached, we want to clear the timeout.
   * This is to prevent the spinner from popping up for quick actions.
   */
  useEffect(() => {
    if (loading) {
      delayTimeoutRef.current = setTimeout(() => {
        setActive(true);
        delayTimeoutRef.current = undefined;
      }, delay);
    } else {
      setActive(false);
    }
    return () => {
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = undefined;
      }
    };
  }, [loading, delay]);

  return (
    <Box
      data-component="OverlaySpinner"
      className={classNames(css.overlayContainer, className)}
      {...rest}
    >
      {active && (
        <Flex className={css.overlay} {...identifyComponent('overlay-spinner')}>
          <Spinner diameter={diameter} variant="dark" />
        </Flex>
      )}
      {children}
    </Box>
  );
};
